import { Link, useLocation } from "react-router-dom";
import { Settings, Key, CreditCard, MessageSquare, ChevronDown, BarChart3, Brain, Users, ListTodo, GraduationCap, MessageCircle, Activity, ThumbsUp, Database, Palette, Mic, CalendarDays, Volume2, Cpu, Server, FileText, Headset, Users2 } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const settingsMenuItems = [
  { path: "/settings/general", label: "General Settings", icon: Settings },
  { path: "/settings/api-keys", label: "API Keys", icon: Key },
  { path: "/settings/branding", label: "Branding", icon: Palette },
  { path: "/settings/openrouter-stats", label: "Stats", icon: BarChart3 },
  { path: "/settings/payment", label: "Payment Settings", icon: CreditCard },
  { path: "/settings/communication", label: "Communication", icon: MessageSquare },
  { path: "/settings/voice-config", label: "Voice Config", icon: Volume2 },
  { path: "/settings/rag-config", label: "RAG Config", icon: Cpu },
  { path: "/settings/rag-infrastructure", label: "RAG Infrastructure", icon: Server },
  { path: "/settings/cdm-documents", label: "CDM Documents", icon: FileText },
  { path: "/settings/zoho", label: "Zoho Desk", icon: Headset },
  { path: "/settings/ai-behavior", label: "AI Behavior", icon: Brain },
];

const dataMenuItems = [
];

const aiMenuItems = [
  { path: "/ai/actors", label: "AI Actors", icon: Users },
  { path: "/ai/voice-models", label: "Voice Models", icon: Mic },
  { 
    path: "/ai/tasks", 
    label: "AI Task", 
    icon: ListTodo,
  },
  { path: "/ai/training", label: "AI Training", icon: GraduationCap },
  { path: "/ai/dashboard", label: "AI Dashboard", icon: Activity },
  { path: "/ai/feedback", label: "Feedback", icon: ThumbsUp },
  { path: "/ai/knowledge-base", label: "Knowledge Base", icon: Database },
  { path: "/ai/thinking-layer", label: "Thinking Layer", icon: Brain },
];

const moderationMenuItems = [
  { path: "/chat-moderation", label: "Chat Moderation", icon: MessageCircle },
];

const appointmentMenuItems = [
  { path: "/appointments", label: "Appointments", icon: CalendarDays },
];

export default function Sidebar({ sidebarOpen }) {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [dataOpen, setDataOpen] = useState(true);
  const [aiOpen, setAiOpen] = useState(true);
  const [aiTaskOpen, setAiTaskOpen] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);

  // Poll for notifications every 30 seconds
  useEffect(() => {
    const checkNotifications = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${BACKEND_URL}/api/admin/notifications/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setNotificationCount(response.data.count || 0);
      } catch (error) {
        console.error("Failed to check notifications", error);
      }
    };

    // Check immediately
    checkNotifications();

    // Then check every 30 seconds
    const interval = setInterval(checkNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <aside
      className={cn(
        "bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0",
        sidebarOpen ? "w-64" : "w-0 overflow-hidden"
      )}
      data-testid="sidebar"
    >
      <div className="h-full flex flex-col">
        {/* Logo */}
        <div className="h-20 px-6 flex items-center border-b border-slate-200">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
            Settings Hub
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-4">
          <div className="space-y-2">
            {/* Settings Section */}
            <div>
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                data-testid="settings-menu-toggle"
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </div>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform",
                    settingsOpen && "rotate-180"
                  )}
                />
              </button>

              {/* Settings Submenu */}
              {settingsOpen && (
                <div className="mt-2 ml-4 space-y-1" data-testid="settings-submenu">
                  {settingsMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                          isActive
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        )}
                        data-testid={`menu-${item.path.split('/').pop()}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Data Section */}
            <div className="mt-4">
              <button
                onClick={() => setDataOpen(!dataOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                data-testid="data-menu-toggle"
              >
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5" />
                  <span>Data</span>
                </div>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform",
                    dataOpen && "rotate-180"
                  )}
                />
              </button>

              {/* Data Submenu */}
              {dataOpen && (
                <div className="mt-2 ml-4 space-y-1" data-testid="data-submenu">
                  {dataMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                          isActive
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        )}
                        data-testid={`menu-${item.path.split('/').pop()}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* AI Section */}
            <div className="mt-4">
              <button
                onClick={() => setAiOpen(!aiOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                data-testid="ai-menu-toggle"
              >
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5" />
                  <span>AI</span>
                </div>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform",
                    aiOpen && "rotate-180"
                  )}
                />
              </button>

              {/* AI Submenu */}
              {aiOpen && (
                <div className="mt-2 ml-4 space-y-1" data-testid="ai-submenu">
                  {aiMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const showBadge = (item.path === "/ai/dashboard") && notificationCount > 0;
                    const hasSubmenu = item.submenu && item.submenu.length > 0;

                    // If item has submenu, render as button
                    if (hasSubmenu) {
                      return (
                        <div key={item.path}>
                          <button
                            onClick={() => setAiTaskOpen(!aiTaskOpen)}
                            className="w-full flex items-center justify-between px-3 py-2 text-slate-600 hover:bg-slate-50 rounded-lg text-sm transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <Icon className="w-4 h-4" />
                              <span>{item.label}</span>
                            </div>
                            <ChevronDown
                              className={cn(
                                "w-4 h-4 transition-transform",
                                aiTaskOpen && "rotate-180"
                              )}
                            />
                          </button>
                          
                          {/* Submenu items */}
                          {aiTaskOpen && (
                            <div className="mt-1 ml-4 space-y-1">
                              {item.submenu.map((subItem) => {
                                const SubIcon = subItem.icon;
                                const subIsActive = location.pathname === subItem.path;
                                
                                return (
                                  <Link
                                    key={subItem.path}
                                    to={subItem.path}
                                    className={cn(
                                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                                      subIsActive
                                        ? "bg-indigo-50 text-indigo-700 font-medium"
                                        : "text-slate-600 hover:bg-slate-50"
                                    )}
                                  >
                                    <SubIcon className="w-4 h-4" />
                                    <span>{subItem.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Regular menu item
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors relative",
                          isActive
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        )}
                        data-testid={`menu-${item.path.split('/').pop()}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                        {showBadge && (
                          <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                            {notificationCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Moderation Section */}
            <div className="mt-4">
              {moderationMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const showBadge = notificationCount > 0;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg font-medium transition-colors relative",
                      isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    {showBadge && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                        {notificationCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Appointments Section */}
            <div className="mt-4">
              {appointmentMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith("/appointments");

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg font-medium transition-colors",
                      isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                    data-testid="menu-appointments"
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
}